<template>
  <LayoutLegal title="General Terms of Service">
    <template #sidebar>
      <ul class="flex flex-col gap-6 leading-snug">
        <li><a href="#1">Definitions</a></li>
        <li><a href="#2">Changes to the Terms of Service</a></li>
        <li><a href="#3">Account Information and Security</a></li>
        <li><a href="#4">Using Our Platform</a></li>
        <li><a href="#5">Monitoring use of Platform and Content</a></li>
        <li><a href="#6">Your Dealings with Other Users</a></li>
        <li><a href="#7">Content Purchases</a></li>
        <li><a href="#8">Promotions and Special Deals</a></li>
        <li><a href="#9">Prohibited Uses</a></li>
        <li><a href="#10">Third-Party Advertising</a></li>
        <li><a href="#11">Copyright Notices/Complaints</a></li>
        <li><a href="#12">Feedback and Unsolicited Ideas</a></li>
        <li><a href="#13">Availability of the Platform; Warranty</a></li>
        <li><a href="#14">Limitations; Indemnity; Waiver of Liability</a></li>
        <li><a href="#15">Dispute Resolution; Class Action Waiver</a></li>
        <li><a href="#16">Applicable Law</a></li>
        <li><a href="#17">Separation of Terms</a></li>
        <li><a href="#18">Assignment</a></li>
        <li><a href="#19">Entire Agreement</a></li>
        <li><a href="#20">Language of the Terms</a></li>
        <li><a href="#21">No Waiver</a></li>
        <li><a href="#22">Waiver of Section 1542</a></li>
        <li><a href="#23">Notices</a></li>
        <li><a href="#24">Force Majeure</a></li>
      </ul>
    </template>

    <h2>General Terms of Service</h2>

    <p class="underline">
      <strong>ARBITRATION NOTICE AND CLASS ACTION WAIVER</strong>
    </p>
    <p>
      <strong>
        IMPORTANT NOTICE: UNLESS YOU OPT OUT OF ARBITRATION WITHIN 30 DAYS OF
        THE DATE YOU FIRST AGREE TO THE TERMS OF SERVICE BY FOLLOWING THE
        OPT-OUT PROCEDURE SPECIFIED IN THE "ARBITRATION" SECTION BELOW, AND
        EXCEPT FOR CERTAIN TYPES OF DISPUTES DESCRIBED IN THE "ARBITRATION"
        SECTION BELOW, YOU AGREE THAT DISPUTES BETWEEN YOU AND FAVORITELY ,
        ("FLLC") WILL BE RESOLVED BY BINDING, INDIVIDUAL ARBITRATION AND YOU ARE
        WAIVING YOUR RIGHT TO A TRIAL BY JURY OR TO PARTICIPATE AS A PLAINTIFF
        OR CLASS MEMBER IN ANY PURPORTED CLASS ACTION OR REPRESENTATIVE
        PROCEEDINGS.
      </strong>
    </p>
    <p>
      The Platform (as defined below) is part of a social media platform that
      allows providers of content that have registered with FLLC to offer access
      to certain features, content, and various other services provided to other
      users of the Platform. Such features, content, and various other services
      may or may not be adult oriented in nature. Access and registration to the
      Platform is free. Paid services on this Platform are required to be
      purchased.
    </p>
    <p>
      This agreement referred to as "General Terms of Service," lists the
      specific terms and conditions that will apply when you use our Platform
      (as defined below). You hereby agree to be bound by this General Terms of
      Service, and the applicable
      <a href="/terms-for-creators">Terms for Creators</a>
      and/or
      <a href="/terms-for-users">Terms for Users</a>
      . In case you don't agree to all the terms and conditions set out in these
      documents, you must not use the Platform. Please read these General Terms
      of Service, your applicable
      <a href="/terms-for-creators">Terms for Creators</a>
      and/or
      <a href="/terms-for-users">Terms for Users</a>
      , and our
      <a href="/privacy">Privacy Policy</a>
      very carefully before using the Platform for any purpose.
    </p>
    <p>
      This agreement contains disclaimers of warranties, limitations on
      liability, releases, a class-action waiver, and the requirement to
      arbitrate all claims that may arise under this agreement. These provisions
      are an essential basis of this agreement.
    </p>
    <p>
      By clicking on the words "I agree," "Submit," or similar syntax, you are
      electronically signing this agreement, and therefore agree to be bound by
      and acknowledge your complete acceptance of all the express and
      incorporated terms of this General Terms of Service. If you do not agree
      to this General Terms of Service or do not meet the age requirements, you
      must not register.
    </p>
    <p>
      You acknowledge and agree that our
      <a href="/privacy">Privacy Policy</a>
      will apply to you when you use the Platform. Our
      <a href="/privacy">Privacy Policy</a>
      explains how we use, store, collect, disclose your personal information
      along with your rights and choices concerning your information. You must
      not use the Platform in case you do not agree with any of the terms set
      forth in the Terms of Service or the ways we use your personal information
      in our
      <a href="/privacy">Privacy Policy</a>
      .
    </p>
    <p>
      Please note that the "Company," "we," "our," or "us" in the Terms of
      Service mean FLLC and all other parent and affiliate entities. FLLC is
      located at 30 Gould Street, Ste 4, Sheridan, WY 82801. In all cases, "we,"
      "our," or "us" includes parents, affiliated, agents, consultants,
      employees, officers, and directors.
    </p>
    <p>
      <strong>Section 230(d) Notice:</strong>
      For users based in the United Sates, note that in accordance with 47
      U.S.C. § 230(d), you are hereby notified that parental control protections
      (including computer hardware, software, or filtering services) are
      commercially available that may help in limiting access to material that
      is harmful to minors. You may find information about providers of these
      protections on the Internet by searching "parental control protection" or
      similar terms.
    </p>
    <p>
      <strong>MINORS PROHIBITED:</strong>
      THE PLATFORM MAY CONTAIN, OR DIRECT YOU TO WEBSITES CONTAINING
      ADULT-ORIENTED CONTENT AND IS NOT INTENDED FOR MINORS. ONLY ADULTS (1) WHO
      CAN LEGALLY ENTER INTO AN AGREEMENT, (2) WHO ARE AT LEAST 18-YEARS OLD AND
      (3) WHO HAVE REACHED THE AGE OF MAJORITY WHERE THEY LIVE MAY ACCESS THE
      PLATFORM. WE FORBID ALL PERSONS WHO DO NOT MEET THESE AGE REQUIREMENTS
      FROM ACCESSING THE PLATFORM.
    </p>
    <p>
      <strong>CHILD PORNOGRAPHY PROHIBITED:</strong>
      WE PROHIBIT PORNOGRAPHIC CONTENT INVOLVING MINORS. We only allow visual
      media of consenting adults for consenting adults on the Platform. If you
      see any visual media, real or simulated, depicting minors engaged in
      sexual activity within the Platform, please promptly report this to us at
      <a href="mailto:legal@favoritely.com">legal@favoritely.com</a>
      . Please include with your report all appropriate evidence, including the
      date and time of identification. We will promptly investigate all reports
      and take appropriate action. We fully cooperate with any law-enforcement
      agency investigating child pornography.
    </p>
    <p>
      <strong>PROSTITUTION AND SEX TRAFFICKING PROHIBITED:</strong>
      WE PROHIBIT THE PLATFORM AND ITS INTERACTIVE SERVICES FROM BEING USED IN
      ANY WAY TO ENGAGE IN, PARTICIPATE IN, ASSIST, SUPPORT, PROMOTE, SOLICIT,
      OR FACILITATE ANY ACT OF PROSTITUTION OF ANOTHER PERSON OR SEX TRAFFICKING
      OF ANOTHER PERSON. This includes using the Platform or its Interactive
      Services to share personal contact details or arrange face-to-face
      meetings. If you see any evidence of the foregoing on the Platform, please
      promptly report this to us at
      <a href="mailto:legal@favoritely.com">legal@favoritely.com</a>
      . Please include with your report all appropriate evidence, including the
      date and time of identification. We will promptly investigate all reports
      and take appropriate action. We will terminate the account of any person
      engaging in any of the foregoing and will report all individuals suspected
      of promoting or facilitating prostitution of another person or sex
      trafficking to the appropriate law enforcement agency. We fully cooperate
      with any law-enforcement agency investigating prostitution or sex
      trafficking.
    </p>
    <p>
      <strong>TERMINATION/SUSPENSION:</strong>
      FLLC may, in its sole discretion, terminate or suspend your access to all
      or part of the Platform at any time, with or without notice, for any
      reason or no reason at all, including, without limitation, violation of
      any term of this Agreement. Any activity which we believe, in our sole
      discretion, to even possibly be illegal, fraudulent, abusive, or in
      violation of our community standards, or our rules, may be grounds for
      termination of your access to all or part of the Platform. In those cases,
      we reserve the right to refer any such activity to any appropriate law
      enforcement agencies. Termination of an Account will be paid for any
      tokens remaining in the Account at the time of termination, as long as the
      amount in the respective account exceeds payment minimums, paid according
      to payment information on file and on the next scheduled payout date. If
      the amount remaining in the account does not exceed payment minimums, the
      amount is forfeited.
    </p>

    <h3 id="1">DEFINITIONS</h3>
    <p>"Account" means an account you create on our Platform.</p>
    <p>
      "User" means users that have registered with the Platform as such and that
      have agreed to abide by this General Terms of Service and the additional
      set of
      <a href="/terms-for-users">Terms for Users</a>
      .
    </p>
    <p>
      "Affiliate Entities," with respect to FLLC, means FLLC, its subsidiaries,
      parent companies, joint ventures, and other corporate entities under
      common ownership and their agents, consultants, employees, officers, and
      directors. It also includes FLLC and its affiliate entities third-party
      content providers, distributors, licensees, or licensors.
    </p>
    <p>
      "Content" means all the data that Creators create, upload, generate or
      transmit on or through the Platform, including but not limited to user's
      profile picture, videos, or other content authored, designed or published
      by the user, if allowed by the Platform. You are responsible that all
      Content shall be in compliance with the Terms of Service at all times.
    </p>
    <p>
      "Content Purchase" means the transaction between a Creator and an User to
      gain access to Content posted by the Creator in the Platform. FLLC is not
      part of any such relationship. FLLC merely facilitates the publication of
      Content in the Platform that allows the User to receive it and the payment
      made by the User to the Creator to gain access to such Content.
    </p>
    <p>
      "Creator" means users that have registered with the Platform as such; that
      have agreed to abide by this General Terms of Service and the additional
      set of
      <a href="/terms-for-creators">Terms for Creators</a>
      . Creators offer their Content through the Platform to Users for purchase
      under the applicable terms and conditions set forth in the Terms of
      Service.
    </p>
    <p>
      "Creator's Offering" means Content posted by the Creators for Users to
      gain access by either purchasing Content, Creators' feed subscriptions,
      paid email chat, tipping, and other services that may be offered in the
      future.
    </p>
    <p>
      "Platform" means our services, content, products, applications,
      <a href="http://www.favoritely.com">www.favoritely.com</a>
      ,
      <a href="http://www.favoritely.com">www.favoritely.com</a>
      , other domains or websites operated by FLLC.
    </p>
    <p>
      "Special Deals" means any special programs, including offers, and special
      gifts that we may offer to certain eligible users from time to time.
    </p>
    <p>
      "Submissions" shall have the meaning assigned to it in Section 13, below.
    </p>
    <p>
      "Terms of Service" means the terms and conditions described in this
      General Terms of Service, together with the terms and conditions set forth
      in the
      <a href="/terms-for-users">Terms for Users</a>
      with respect to Users; and the
      <a href="/terms-for-creators">Terms for Creators</a>
      with respect to Creators.
    </p>
    <p>
      "
      <a href="/terms-for-users">Terms for Users</a>
      " means the additional set of terms and conditions that govern the
      relationship of the Users with us and other users and/or Creators while
      using the Platform. Whenever this document refers to the Terms of Service,
      the
      <a href="/terms-for-users">Terms for Users</a>
      are considered to be part of such Terms of Service for the applicable
      User.
    </p>
    <p>
      "
      <a href="/terms-for-creators">Terms for Creators</a>
      " means the additional set of terms and conditions that govern the
      relationship of the Creator with us and other users and/or Users while
      using the Platform. Whenever this document refers to the Terms of Service,
      the
      <a href="/terms-for-creators">Terms for Creators</a>
      are deemed to be part of such Terms of Service for the Creators.
    </p>

    <h3 id="2">CHANGES TO THE TERMS OF SERVICE</h3>
    <p>
      We reserve the right to change, modify, add, or remove portions of the
      Terms of Service at any time and at our sole discretion. The amended Terms
      of Service may be posted on our website. Any material changes may also be
      additionally notified through an e-mail message or messaging within the
      Platform. All the changes will be effective when posted, unless we state
      otherwise. Any new versions of the Terms of Service and other policies,
      rules or codes, will be accessible at www.favoritely.com. In the event
      that you continue to use the website after the changes are posted, you
      automatically agree that the changes apply to your continued use of the
      Platform.
    </p>
    <p>
      In the event of a dispute, the Terms of Service version applicable would
      be the version in effect at the time we received actual notice of the
      dispute.
    </p>
    <p>
      In case of a conflict between any provisions or information of the Terms
      of Service or our
      <a href="/privacy">Privacy Policy</a>
      with other terms or policies, the Terms of Service and the
      <a href="/privacy">Privacy Policy</a>
      as applicable shall govern.
    </p>

    <h3 id="3">ACCOUNT INFORMATION AND SECURITY</h3>
    <p>
      You will be required to create an Account and select a password for using
      the Platform. We may also require personal information such as your full
      name, date of birth, contact details including e-mail address, and, in
      some cases, payment information. We confirm that all the information
      obtained from you will be used and held in accordance with our Privacy
      Policy.
    </p>
    <p>
      You agree and confirm that all the information you provide will be
      complete and up-to-date especially your contact details.
    </p>
    <p>
      We will not be responsible for maintaining the security of your Account
      and such responsibility lies entirely with you. You shall not share your
      personal and Account details with anyone else except yourself or allow
      anyone else to use or access your Account. All acts performed through your
      Account will be treated as being performed by you only and hence you fully
      accept the responsibility for all acts committed or done using your
      Account, whether or not authorized by you, including any purchases made
      using any payment method registered or authorized by you (for example,
      credit card). In case someone else uses your Account and any losses occur
      to the us or any other user of the Platform, you will be held fully liable
      for any such losses incurred by us or any other user of the Platform.
    </p>
    <p>
      We expect to be informed immediately in case you become aware of any
      actual or suspected theft, loss, fraud, or any unauthorized use of your
      Account or password.
    </p>

    <h3 id="4">USING OUR PLATFORM</h3>
    <p>Who can use our Platform</p>
    <p>
      We want everyone to use the Platform. However, there are certain
      restrictions on who can use it.
    </p>
    <p>You may not use our Platform:</p>
    <ul class="leading-loose">
      <li>If you cannot legally enter into a binding contract with us.</li>
      <li>
        If you are under 18 years of age or under the age of majority where you
        are located. In such cases, you must not use our Platform, and you must
        not create an Account, or submit personal information (such as your
        name, address, telephone number, or email address) to us.
      </li>
      <li>
        If you are not allowed to receive products, including services or
        software, from the United States, for example if you are located in a
        country embargoed by the United States or if you are on the U.S.
        Treasury Department's list of Specially Designated Nationals.
      </li>
      <li>
        If accessing or publishing public nudity is illegal in the country or
        the locality where you are accessing the Platform.
      </li>
      <li>
        If you are in violation of the Terms of Service or you intend to violate
        the Terms of Service.
      </li>
      <li>If you are a convicted sex offender.</li>
      <li>
        If you have been banned previously from using the website, unless we
        have reversed that ban, at our sole discretion.
      </li>
    </ul>

    <p class="underline">Adult-Oriented Content</p>
    <p>
      The Platform contains uncensored material that may be sexually explicit,
      unsuitable for minors. Only adults (1) who can legally enter into an
      agreement, (2) who are at least 18-years old and (3) who have reached the
      age of majority where they live may access the Platform. If you do not
      meet these age requirements, you must not access the website and must
      leave now. By accessing the Platform, you state that the following facts
      are accurate:
    </p>
    <ul class="leading-loose">
      <li>
        You are at least 18-years old, have reached the age of majority where
        you live, and you have the legal capacity to enter this agreement;
      </li>
      <li>
        You are aware of the adult nature of the content available on the
        Platform, and you are not offended by visual images, verbal
        descriptions, and audio sounds of a sexual nature, which may include
        graphic visual depictions and descriptions of nudity and sexual
        activity;
      </li>
      <li>
        You are familiar with your community's laws affecting your right to
        access adult-oriented materials;
      </li>
      <li>
        You have the legal right to access adult-oriented materials, and we have
        the legal right to transmit them to you;
      </li>
      <li>
        You are voluntarily requesting adult-oriented materials for your own
        private enjoyment;
      </li>
      <li>
        You are not accessing the website from a place, country, or location in
        which doing so would, or could be considered a violation of applicable
        law; and
      </li>
      <li>
        You will not share these materials with a minor or otherwise make them
        available to a minor.
      </li>
      <li>
        You acknowledge that you are aware that you may find the type of Content
        on the Platform to be obscene, offensive, or otherwise objectionable and
        you agree to take this into account when opting to access and view the
        Content. Therefore, you waive any claims you may have against us based
        on the type of content posted on the Platform. We will not be
        responsible to you for any loss or damage as a result of your accessing
        or viewing the type of Content posted on our Platform.
      </li>
    </ul>
    <p class="underline">Accessing our Platform</p>
    <p>
      To access many of the Platform's features, you must create an Account.
      Registration is free and for a single user only. To register, you must
      complete the registration process by providing accurate information as
      prompted by the applicable registration form. You also must choose a
      password and a user name. The username must not be offensive and must not
      infringe another person's service mark, trademark, or trade name. By
      creating an Account, you state that (a) all account registration, and
      profile information you provide is your own and is accurate; (b) if you
      previously had an Account on the Platform, your old account was not
      terminated or suspended for violation of this agreement; and (c) you are
      creating an Account for your own personal use and you will not sell, rent,
      or transfer your Account to any third party.
    </p>
    <p>
      We will only provide the Platform. To download and use our Platform, you
      will provide the equipment such as computer, phone, tablet and any other
      device required to connect with the Internet and pay any necessary fees to
      connect to the app stores and Internet, and for data or cellular usage.
    </p>
    <p class="underline">Service Changes and Limitations</p>
    <p>
      We may from time to time make you update the Platform or your software.
      You will be required to accept these updates to the Platform as well as to
      the Terms of Service, and our
      <a href="/privacy">Privacy Policy</a>
      . The updates may be performed remotely without notifying you, including
      to our software residing on your computer or mobile device.
    </p>
    <p>
      Your right to use our website or any part of the Platform is reserved with
      us and we may at any time stop to offer and/or support the Platform,
      either permanently or temporarily. In such an event, your right to use the
      Platform or any part of it will be terminated or suspended automatically
      and we will not be required to provide benefits, refunds, or other
      compensation to you in connection with discontinued elements of the
      Platform previously earned or purchased, unless applicable law requires
      otherwise.
    </p>
    <p>
      WE MAY, AT OUR SOLE DISCRETION, LIMIT, SUSPEND, TERMINATE, MODIFY, OR
      DELETE ACCOUNTS OR ACCESS TO THE PLATFORM OR ANY PORTION OF THEM; PROHIBIT
      ACCESS TO OUR PLATFORM, AND ITS CONTENT, AND TOOLS; OR DELAY OR REMOVE
      HOSTED CONTENT, AND WE ARE UNDER NO OBLIGATION TO COMPENSATE YOU FOR ANY
      LOSSES OR RESULTS. YOU MAY NOT BRING ANY CLAIM AGAINST US FOR LIMITING
      ACCESS TO OUR PLATFORM, SUSPENSION, TERMINATION, OR DELETION OF ANY
      ACCOUNT FOR ANY REASON. IF ANY SUCH CLAIM IS BROUGHT AGAINST US, YOU ARE
      RESPONSIBLE FOR ALL DAMAGES CAUSED TO US OR EXPENSES THAT WE WOULD INCUR
      TO MAINTAIN LIMITING YOUR ACCESS TO OUR PLATFORM, SUSPENSION, TERMINATION,
      OR DELETION OF ANY ACCOUNT, INCLUDING ATTORNEY FEES AND COSTS. THE TERMS
      OF SERVICE REMAIN IN EFFECT IF THE ACCOUNT IS ACTIVE OR INACTIVE OR YOU NO
      LONGER HAVE AN ACCOUNT.
    </p>
    <p>
      This does not apply to users located in the EEA. If you are located in the
      EEA, we will endeavor to give you at least one month's notice of any
      material changes before they take effect, and if you feel said changes are
      unsatisfactory, you can choose to cancel your access to our Platform under
      the Terms of Service.
    </p>
    <p class="underline">Deleting your Account</p>
    <p>
      At any time, you may stop using our Platform or request that we delete
      your Account by following the instructions in our
      <a href="/privacy">Privacy Policy</a>
      . We are not required to provide refunds, benefits, or other compensation
      if you request deletion of your Account, unless applicable law requires
      otherwise.
    </p>
    OWNERSHIP; LIMITED LICENSE
    <p class="underline">Our Platform</p>
    <p>
      Our Platform is comprised of works that are owned or licensed by us and
      they are protected by patents, trademarks, copyrights, trade dress, and
      other worldwide intellectual property rights and other applicable laws,
      rules, or regulations. All such rights are reserved. The Terms of Service
      are not intended to grant and do not grant to you any right, title, or
      interest beyond the limited access to such materials to allow the proper
      use of the Platform and limited to the Terms of Service.
    </p>
    <p>
      You agree that we will bear no liability to you or any third party for any
      damage or loss arising from unauthorized uses of the Platform.
    </p>
    <p>
      This agreement permits you to use the Platform for your personal,
      non-commercial use only. You must not reproduce, distribute, modify,
      create derivative works of, publicly display, publicly perform, republish,
      download, store, or transmit any of the material on the Platform, except
      as follows:
    </p>
    <ul class="leading-loose">
      <li>
        Your computer may temporarily store copies of those materials in RAM
        incidental to your accessing and viewing those materials.
      </li>
      <li>
        You may store files that are automatically cached by your Web browser
        for display enhancement purposes.
      </li>
      <li>
        You may print or download one copy of a reasonable number of pages of
        the Platform for your own personal, noncommercial use and not for
        further reproduction, publication, or distribution.
      </li>
      <li>
        You may download or stream any audiovisual content to which you have
        properly gained access solely for your personal, noncommercial use and
        not for further reproduction, publication, or distribution.
      </li>
      <li>
        If we provide desktop, mobile, or other applications for download, you
        may download a single copy to your computer or mobile device solely for
        your own personal, non-commercial use, on condition that you agree to be
        bound by the end user license agreement for those applications.
      </li>
      <li>
        If we provide social media features with certain content, you may take
        those actions as are enabled by those features.
      </li>
    </ul>
    <p>
      We may take action against you, if you breach the Terms of Service, or any
      of our other terms that apply to you. Such action may include, but not
      limited to, terminating your account. In addition, you may be breaking the
      law, including breaches or violations of our intellectual property rights.
    </p>
    <p>
      ANY ATTEMPT BY YOU TO DISRUPT OR INTERFERE WITH THE PLATFORM, INCLUDING
      WITHOUT LIMITATION UNDERMINING OR MANIPULATING THE LEGITIMATE OPERATION OF
      THE PLATFORM, IS A BREACH OF THE TERMS OF SERVICE AND OUR POLICY AND MAY
      BE A BREACH OR VIOLATION OF CRIMINAL AND CIVIL LAWS.
    </p>
    <p>
      The data and materials on the Platform, including the text, graphics,
      interactive features, logos, photos, music, videos, software, and all
      other audible, visual, or downloadable materials, as well as the
      selection, organization, coordination, compilation, and overall look and
      feel of the Platform (collectively, the "Materials") are either the
      intellectual property of FLLC, its licensors, and its suppliers, or have
      the authority to use them. The Materials are protected by United States
      copyright, trade dress, patent, trademark, and other laws, international
      conventions, and proprietary rights and all rights to the Materials remain
      with the Company, its licensors, or its suppliers, as the case may be. The
      users do not acquire any ownership rights in the Platform. Except as we
      expressly authorize, users will not reproduce, distribute, modify, create
      derivative works of, publicly display, publicly perform, republish,
      download, store, or transmit any of the Materials. If, with authorization,
      you download or print a copy of the Materials for use, you must retain all
      copyright, trademark, or other proprietary notices. We reserve all rights
      not expressly granted in and to the Platform and the Materials.
    </p>
    <p>
      Our name and logo; the term FAVORITELY; and all related names, logos,
      product and service names, designs, and slogans are the our service marks
      and trademarks. You will not use these marks without our written
      permission. All other names, logos, product and service names, designs,
      and slogans on the Platform are the service marks and trademarks of their
      respective owners.
    </p>
    <p>
      We hereby grant you a non-exclusive, non-transferable, non-sub-licensable
      (except as otherwise provided in this agreement), revocable, limited
      license to access and use the Platform and the related software in
      accordance with this agreement (including the right to create a profile,
      upload content, and sell content through the Platform). Any use other than
      as expressly permitted by this agreement is strictly prohibited.
    </p>

    <p class="underline">Company Proprietary Rights</p>
    <p>
      The data and materials on the Platform, including the text, graphics,
      interactive features, logos, photos, music, videos, software, and all
      other audible, visual, or downloadable materials, as well as the
      selection, organization, coordination, compilation, and overall look and
      feel of the Platform (collectively, the "Materials") are either the
      intellectual property of FLLC, its licensors, and its suppliers, or have
      the authority to use them. The Materials are protected by United States
      copyright, trade dress, patent, trademark, and other laws, international
      conventions, and proprietary rights and all rights to the Materials remain
      with the Company, its licensors, or its suppliers, as the case may be. The
      users do not acquire any ownership rights in the Platform. Except as we
      expressly authorize, users will not reproduce, distribute, modify, create
      derivative works of, publicly display, publicly perform, republish,
      download, store, or transmit any of the Materials. If, with authorization,
      you download or print a copy of the Materials for use, you must retain all
      copyright, trademark, or other proprietary notices. We reserve all rights
      not expressly granted in and to the Platform and the Materials.
    </p>
    <p>
      Our name and logo; the term FAVORITELY; and all related names, logos,
      product and service names, designs, and slogans are the our service marks
      and trademarks. You will not use these marks without our written
      permission. All other names, logos, product and service names, designs,
      and slogans on the Platform are the service marks and trademarks of their
      respective owners.
    </p>
    <p>
      We hereby grant you a non-exclusive, non-transferable, non-sub-licensable
      (except as otherwise provided in this agreement), revocable, limited
      license to access and use the Platform and the related software in
      accordance with this agreement (including the right to create a profile,
      upload content, and sell content through the Platform). Any use other than
      as expressly permitted by this agreement is strictly prohibited.
    </p>
    <p class="underline">Your Account</p>
    <p>
      Your Account, regardless of any other statement in the Terms of Service
      that apply to features you may choose to use, is not your property. You do
      not own any Account that you create on our Platform, it only gives you a
      limited license and right to use your Account.
    </p>
    <p>
      WE MAY DELETE OR TERMINATE ACCOUNTS THAT ARE INACTIVE (I.E., NOT LOGGED
      INTO) FOR 180 DAYS IN ORDER TO AVOID RETAINING DATA THAT IS NO LONGER
      NEEDED AND/OR TO IMPROVE OUR PLATFORM.
    </p>
    <p class="underline">User Content</p>
    <p>
      You may transmit, upload, publish or post Content onto the Platform, and
      in doing so, you agree that:
    </p>
    <ul class="leading-loose">
      <li>it will be accurate;</li>
      <li>it will be non-confidential;</li>
      <li>it will not be in violation of any law;</li>
      <li>
        it will not be in violation of any contractual restrictions or rights of
        other parties, and that you have acquired requisite permission to use
        the User Content from the other party whose intellectual property or
        other information is contained within the User Content;
      </li>
      <li>
        it will be free from any worms, spyware, viruses, adware, worms, or
        other malicious code;
      </li>
      <li>it will not violate the Terms of Service.</li>
    </ul>
    <p>
      You are solely responsible for backing up and securing your Content as it
      will be processed by us in accordance with our
      <a href="https://favoritely.com/privacy">
        <a href="/privacy">Privacy Policy</a>
      </a>
      .
    </p>
    <p>
      We will consider any Content that you publish, post, or transmit as
      non-confidential and non-proprietary. You give us a perpetual and
      irrevocable (other than as provided below or in our
      <a href="/privacy">Privacy Policy</a>
      ), worldwide, royalty free, non-exclusive, license to use, reproduce,
      distribute, prepare derivative works of, display, and perform your Content
      and any modified and derivative works thereof in connection with the
      website, including in marketing and promotions while you will retain all
      of your ownership rights in your Content. You waive any moral rights you
      may have in any Content (like the right to be identified as the author of
      the Content or the right to object to a certain use of that Content) to
      the extent allowed by applicable laws.
    </p>
    <p>
      If, in our opinion, any Content does not comply with the Terms of Service,
      we reserve the right to remove such Content from the Platform. In case we
      receive a notice or legitimate complaint from a third party that any
      Content constitutes a breach of such third party's intellectual property
      rights or their right to privacy, we will have the right to disclose your
      identity to such third party, and the user waives any privacy complaints
      to us in such an event. Wherever legally required to do so, we will
      disclose your Content to other third parties or government bodies if they
      so request and require.
    </p>
    <p>
      When you request deletion of your Account by submitting a request to
      <a href="mailto:support@favoritely.com">support@favoritely.com</a>
      as described in our
      <a href="/privacy">Privacy Policy</a>
      , our license to your Content ends, with the following exceptions:
    </p>
    Any Content submitted in response to promotions. Such content will be
    subjected to any terms provided in the promotion; Content either shared with
    others, which they have not deleted or already used publicly as allowed
    under the Terms of Service; and In case any Content is subject to a separate
    license with us, such content will be subjected to the terms of such
    license.
    <p>
      We will take reasonable steps to remove your Content from active use,
      which may include suppression of your Content in our systems, if you
      request deletion of your Content. Content may however continue to persist
      in our systems, along with in back-up copies. If we reasonably believe it
      is legally required, we may retain copies of any Content with us.
    </p>
    <p>
      We cannot guarantee that other users will not use the ideas and
      information that you share when you post, publish, or transmit your
      observations and comments on the website, such as in blogs, forums, and
      chat features. It is recommended to not post any idea or information that
      you would like to keep confidential and/or don't want others to use. WE
      WILL NOT BE HELD RESPONSIBLE FOR ANY OTHER PERSON'S USE OR APPROPRIATION
      OF ANY CONTENT OR INFORMATION YOU TRANSMIT, POST, OR PUBLISH IN ANY
      FORUMS, CHAT ROOMS OR BLOGS.
    </p>
    <h3 id="5">MONITORING USE OF PLATFORM AND CONTENT</h3>
    <p>
      We have no obligation to monitor the Platform for illegal or inappropriate
      Content or Content that violates the Terms of Service or the conduct of
      other users. We will not be held responsible for information, materials,
      products, or services provided by other users (for instance, in their
      profiles) and Content is not approved by us. In case of suspected
      violation of the Terms of Service or misuse of the Platform, please use
      the "Report Abuse" link provided in the Platform or contact us at
      <a href="mailto:legal@favoritely.com">legal@favoritely.com</a>
      .
    </p>
    <p>
      We have the right, in our sole discretion, to record, monitor, or store
      your communications and interactions with the Platform or your
      communications with the Platform or any other user when you are using the
      Platform. At our discretion, we may choose to edit, refuse to post, or
      remove any Content from the Platform. If we determine that your
      communications or Content violate the Terms of Service, we have the right,
      in our sole discretion and without notice, to disable your ability to post
      Content or to communicate with other users, or we may terminate your
      access to the Platform entirely.
    </p>
    <h3 id="6">YOUR DEALINGS WITH OTHER USERS</h3>
    <p>
      You are responsible for your interactions with other users. If you have a
      problem with another user, we are not required to get involved, but we can
      if we desire. Any Content Purchase is considered to be an agreement
      between the Creator and the purchasing User, governed by the applicable
      terms of the Terms of Service and the specific terms set by the Creator
      offering its content through the Platform.
    </p>
    <p>
      If you have a dispute with another user, you release us and all of our
      Affiliate Entities from responsibility, claims, demands, and/or damages
      (actual or consequential) of every kind and nature, whether known or
      unknown, resulting from that dispute or connected to that dispute. This
      includes damages for loss of profits, goodwill, use, or data. This does
      not apply to users located in the EEA. If you are located in the EEA, your
      liability vis-à-vis is as set forth by the law applicable in the country
      where you reside.
    </p>
    <h3 id="7">CONTENT PURCHASES</h3>
    <p>
      The Platform allows Creators to offer and sell access to the Content they
      post in the Platform and Users to purchase access to such Creator's
      Offering in exchange for a one-time or recurring fee, as determined by
      each Creator. Creators set their own pricing. Pricing excludes any taxes
      or currency transmission charges, which are extra costs charged to you.
      Buying paid services from one Creator does not grant you access to Content
      from another Creator. You must separately buy Content to each Creator you
      want access to. All relevant rules for payments for Content Purchases are
      set forth in the
      <a href="/terms-for-users">Terms for Users</a>
      .
    </p>
    <h3 id="8">PROMOTIONS AND SPECIAL DEALS</h3>
    <p>
      We may offer limited promotions and Special Deals. Please review terms
      associated with any promotion and Special Deals as they will apply to them
      in addition to the Terms of Service.
    </p>
    <p>
      It is not compulsory for us to give, and it is not compulsory for you to
      accept any Special Deals. All Special Deals will be at our sole discretion
      and no Special Deals can be transferred, redeemed, or exchanged for other
      things of value. In the event you accept a Special Deal, you may be
      required to sign a liability release and a declaration of eligibility to
      receive the Special Deal. Before you accept the Special Deal, we will
      disclose any taxes, or other charges, for the Special Deal. Once accepted,
      you assume all liabilities associated with the Special Deal.
    </p>
    <p>
      Some Creators may promote competitions, promotions, prize draws, and other
      similar opportunities on the website ("Third-Party Competitions"). We are
      not the sponsor or promoter of such Third-Party Competitions and do not
      bear any responsibility or liability for the actions or inactions of any
      third parties who organize, administer, or are otherwise involved in any
      promotion of such Third-Party Competitions. If Users wish to participate
      in any Third-Party Competitions, Users are responsible for reading and
      ensuring that you understand the applicable rules and any eligibility
      requirements and are lawfully able to participate in such Third-Party
      Competitions in the User's country of residence.
    </p>
    <h3 id="9">PROHIBITED USES</h3>
    <p>
      Notwithstanding other prohibitions set for the in the Terms of Service, we
      have a list of prohibited activities which you agree you will not do under
      any circumstances while using the Platform. This prohibitions include:
    </p>
    <ul class="leading-loose">
      <li>
        To publish, post, submit, upload or transmit any Content that: (i)
        infringes, misappropriates or violates a third party's patent,
        copyright, trademark, trade secret, moral rights or other intellectual
        property rights, or rights of publicity or privacy; (ii) violates, or
        encourages any conduct that would violate the Terms of Service, any
        applicable law or regulation or would give rise to civil liability;
        (iii) is fraudulent, false, misleading or deceptive; (iv) is defamatory,
        obscene, vulgar, scatological or offensive; (v) promotes discrimination,
        bigotry, racism, hatred, harassment or harm against any individual or
        group; (vi) is violent or threatening or promotes violence or actions
        that are threatening to any person or entity; or (vii) promotes illegal
        or harmful activities or substances.
      </li>
      <li>
        To display, mirror, use or frame the Platform, or any element within the
        Platform, our name, trademark, logo or other proprietary information, or
        the layout and design of any page or form contained on a page, without
        our express written consent.
      </li>
      <li>
        To access or tamper with in any way, or use non-public areas of the
        Platform, the Platform's computer systems, or the technical delivery
        systems of the Platform's providers.
      </li>
      <li>
        To attempt probing, scanning, or testing the vulnerability of our
        network or system or breach of any authentication or security measures.
      </li>
      <li>
        To attempt avoiding, bypassing, removing, deactivating, impairing,
        descrambling or otherwise circumventing any technological measure
        implemented by us or any of our providers or any other third party
        (including another user).
      </li>
      <li>
        To use any software, tool, device, agent, engine, or mechanism
        (including crawlers, data mining tools, robots, spiders, or the like)
        other than the software and/or search agents provided by the Platform or
        other generally available third-party web browsers for attempting to
        access or search the Platform or download Content from the Platform.
      </li>
      <li>
        To attempt sending any unauthorized or unsolicited advertising,
        promotional or marketing materials, spams, chain letters, emails, junk
        mails, or any other form of solicitation.
      </li>
      <li>
        To use, without our express written consent, metadata utilizing our
        trademark, logo URL or product name or any meta tags or other hidden
        text.
      </li>
      <li>
        To use the Platform in any manner which is not permitted by the Terms of
        Service for any commercial purpose or the benefit of any third party.
      </li>
      <li>
        To use the Platform in any way for sending deceptive, altered, or false
        source-identifying information or forging any TCP/IP packet header or
        any part of the header information in any newsgroup posting or email.
      </li>
      <li>
        To attempt reverse engineer, disassemble, undo, decompile, decode, or
        decipher any of the software used to provide the Platform.
      </li>
      <li>
        To interfere with, or attempting to interfere with, the access of any
        host, network or user, including, without limitation, sending a virus,
        flooding, spamming, overloading, or mail-bombing the website.
      </li>
      <li>
        To attempt, without express permission, to collect or store any
        personally identifiable information of the other users from the
        Platform.
      </li>
      <li>
        To attempt impersonating or misrepresenting your affiliation with any
        person or entity.
      </li>
      <li>To attempt violating any applicable law or regulation.</li>
      <li>
        To attempt to encourage or enable any other individual to do any of the
        foregoing activities.
      </li>
      <li>
        You will not use the Platform to arrange face-to-face meetings offline
        with any Creator or other user for the purpose of sex.
      </li>
      <li>
        To attempt to commercialize with your access to the Platform. You are
        not allowed to sell, transfer or share your access to the Platform.
      </li>
    </ul>
    <h3 id="10">THIRD-PARTY ADVERTISING</h3>
    <p>
      Our Platform may feature advertisements from us or third parties. Our
      <a href="/privacy">Privacy Policy</a>
      explains what information we share with advertisers. Please read it.
    </p>
    <p>
      Sometimes we provide links on the Platform to other third-parties'
      websites or to others who invite you to participate in a promotional offer
      and offer you some feature of the website or upgrade in exchange. Any
      charge or obligation you take on in dealing with these other companies is
      your responsibility.
    </p>
    <p>
      We are not responsible for any third-party website that we link toon our
      website, and such a link does not mean we endorse or approve that linked
      site or any information you obtain from it. We are not liable for any
      claim relating to any content, goods, and/or services of third parties.
    </p>
    <p>
      Please also note that the linked third-party sites are not under our
      control and may collect data or ask you to provide them with personal or
      other information, or they may automatically collect information from you.
      When you use these third-party sites and services, the third-party company
      may (or may not) ask you for permission to access your information and
      content. We are not responsible for these other companies' content,
      business practices, or privacy policies, or for how they collect, use, or
      share the information they get from you.
    </p>
    <h3 id="11">COPYRIGHT NOTICES/COMPLAINTS</h3>
    <p>
      We require all users of the Platform to comply with copyright and related
      laws. We are committed to complying with copyright and related laws. We
      prohibit storing any material or content on, or disseminating any material
      or content over the Platform in any manner that constitute an infringement
      of third party intellectual property rights, including rights granted by
      copyright law to the owners of the copyrighted works. Owners of
      copyrighted works in the United States may report alleged infringements by
      taking advantage of certain provisions of the US Digital Millennium
      Copyright Act of 1998 (the "DMCA") - especially the owners who believe
      that their rights under copyright law have been infringed. Without
      obtaining the prior written consent from the owner of proprietary and
      Intellectual Property rights, we prohibit you from modifying, reproducing,
      posting, or distributing in any way any copyrighted material, trademarks,
      or other proprietary information belonging to such owners. Upon receipt of
      proper notification by the copyright owner or the copyright owner's legal
      agent, we can terminate privileges of any user who repeatedly infringes
      the copyright rights of others.
    </p>
    <p>
      We encourage you to contact us immediately in case you believe that a
      posted message or any other material posted on the website is
      objectionable or infringing any proprietary work. Upon receipt of a notice
      or complaint, we will remove or stop access to, the material claimed to be
      infringing and will follow the due procedures specified in the DMCA to
      resolve the claim between the complainant and the alleged infringer who
      posted the content in issue.
    </p>
    <p>
      If you believe that your work has been copied and posted on the website in
      a way that constitutes copyright infringement, please provide our
      designated agent (indicated below) with the following information:
    </p>
    <p>
      (a) An electronic or physical signature of the person authorized to act on
      behalf of the owner of the copyright or other intellectual property
      interest;
    </p>
    <p>
      (b) A description of the copyrighted work or other intellectual property
      that you claim has been infringed;
    </p>
    <p>
      (c) A description of where the material that you claim is infringing is
      located on the Platform;
    </p>
    <p>(d) Your address, telephone number, and email address;</p>
    <p>
      (e) A statement by you that you have a good faith belief that the disputed
      use is not authorized by the copyright or intellectual property owner, its
      agent, or the law; and
    </p>
    <p>
      (f) A statement by you, made under penalty of perjury, that the
      information contained in your report is accurate and that you are the
      copyright or intellectual property owner or authorized to act on the
      copyright or intellectual property owner's behalf.
    </p>
    <p>
      (g) Our designated agent for notice of claims of copyright infringement
      can be reached as follows:
    </p>
    <p>
      By E-Mail:
      <a href="mailto:support@favoritely.com">support@favoritely.com</a>
      Subject line: DMCA
    </p>

    <h3 id="12">FEEDBACK AND UNSOLICITED IDEAS</h3>

    <p>
      We are always pleased to receive feedback/ideas regarding existing
      features with the exception of unsolicited ideas and expressions. We will
      consider only specific feedback/ideas on existing features, and reserves
      the right to discard ideas that our policy will not permit us to accept or
      consider. You can post these feedbacks/ideas at:
      <a href="mailto:support@favoritely.com">support@favoritely.com</a>
      .
    </p>
    <p>
      Any feedback you provide is deemed to be non-confidential and
      non-proprietary. We may use such information on an unrestricted basis,
      without having an obligation to compensate you or any other third party.
    </p>
    <p>
      We strictly do not accept or consider any unsolicited suggestions,
      proposals, ideas, materials, or comments ("Submissions"). In case you
      still send your Submissions, then regardless of the contents of your
      Submissions, the following terms shall apply in which you agree that:
    </p>
    <ul class="leading-loose">
      <li>
        We will consider the Submissions to be non-confidential and
        non-proprietary.
      </li>
      <li>
        We shall have no obligations whatsoever concerning the Submissions,
        including but not limited to, no obligation to return any materials,
        keep the materials or acknowledge receipt of any Submissions.
      </li>
      <li>
        We may use, redistribute, or disclose the Submissions for any purpose
        and in any way, without any compensation to you or any third party.
      </li>
    </ul>

    <h3 id="13">AVAILABILITY OF THE PLATFORM; WARRANTY</h3>
    <p class="underline">Availability of the Platform</p>
    <p>
      FLLC and its Affiliate Entities, make no guarantees or promises that the
      Platform or any content provided on the Platform will always be available
      without any interruption or will be error-free. We may for business and
      operational reasons, withdraw, stop, suspend, interrupt or restrict the
      availability of all or any part of our Platform.
    </p>
    <p class="underline">Warranty Disclaimers</p>
    <p>
      USE OF THE PLATFORM IS AT YOUR SOLE RISK. IT IS PROVIDED ON AN "AS IS"
      BASIS. TO THE EXTENT PERMITTED BY APPLICABLE LAW, WE MAKE NO WARRANTIES,
      CONDITIONS, OR OTHER TERMS OF ANY KIND, EITHER EXPRESS OR IMPLIED, ABOUT
      THE PLATFORM. FLLC AND ITS AFFILIATE ENTITIES DISCLAIM ANY WARRANTIES OF
      TITLE OR IMPLIED WARRANTIES, CONDITIONS, OR OTHER TERMS OF
      NON-INFRINGEMENT, MERCHANTABILITY, QUIET ENJOYMENT, OR FITNESS FOR A
      PARTICULAR PURPOSE.
    </p>
    <p>
      If your state or country does not allow these disclaimers, they do not
      apply to you. If your state or country requires a certain period for which
      a warranty applies, it will be either the shorter of 30 days from your
      first use of the website or the shortest period required by law.
    </p>

    <h3 id="14">LIMITATIONS; INDEMNITY; WAIVER OF LIABILITY</h3>
    <p>
      YOU ACKNOWLEDGE THAT FLLC, AND ITS AFFILIATE ENTITIES ARE NOT LIABLE -
    </p>
    <p>
      (1) FOR ANY INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL
      DAMAGES, INCLUDING FOR LOSS OF PROFITS, GOODWILL, OR DATA, IN ANY WAY
      WHATSOEVER ARISING OUT OF THE USE OF, OR INABILITY TO USE, THE PLATFORM;
      OR
    </p>
    <p>
      (2) FOR THE CONDUCT OF THIRD PARTIES, INCLUDING OTHER USERS OF THE
      PLATFORM AND OPERATORS OF EXTERNAL SITES.
    </p>
    <p>
      THE RISK OF USING THE PLATFORM AND EXTERNAL SITES RESTS ENTIRELY WITH YOU
      AS DOES THE RISK OF INJURY FROM THE WEBSITE AND EXTERNAL SITES.
    </p>
    <p>
      TO THE FULLEST EXTENT ALLOWED BY ANY LAW THAT APPLIES, THE DISCLAIMERS OF
      LIABILITY IN THE TERMS OF SERVICE APPLY TO ALL DAMAGES OR INJURY CAUSED BY
      THE PLATFORM, OR RELATED TO USE OF, OR INABILITY TO USE, THE PLATFORM,
      UNDER ANY CAUSE OF ACTION IN ANY JURISDICTION, INCLUDING, WITHOUT
      LIMITATION, ACTIONS FOR BREACH OF WARRANTY, BREACH OF CONTRACT, OR TORT
      (INCLUDING NEGLIGENCE).
    </p>
    <p>
      TO THE MAXIMUM EXTENT PERMISSIBLE UNDER APPLICABLE LAWS, THE TOTAL
      LIABILITY OF FLLC AND ITS AFFILIATE ENTITIES IS LIMITED TO THE TOTAL
      AMOUNT YOU HAVE PAID US, AND/ OR OUR AFFILIATE ENTITIES FOR USE OF THE
      PLATFORM, PRODUCTS OR USER CONTENT IN THE ONE HUNDRED AND EIGHTY DAYS
      (180) DAYS IMMEDIATELY PRECEDING THE DATE ON WHICH YOU FIRST ASSERT ANY
      SUCH CLAIM.
    </p>
    <p>
      IF YOU HAVE NOT PAID US AND/OR OUR AFFILIATE ENTITIES ANY AMOUNT IN THE
      ONE HUNDRED AND EIGHTY DAYS (180) DAYS IMMEDIATELY PRECEDING THE DATE ON
      WHICH YOU FIRST ASSERT ANY SUCH CLAIM, YOUR SOLE AND EXCLUSIVE REMEDY FOR
      ANY DISPUTE WITH US OR ANY OF OUR AFFILIATE ENTITIES IS TO STOP USING THE
      PLATFORM AND TO CANCEL YOUR ACCOUNT.
    </p>
    <p>
      Some states or countries do not allow the exclusion of certain warranties
      or the limitations/exclusions of liability described above, which means
      these limitations/exclusions may not apply to you if you reside in one of
      those states or countries.
    </p>
    <p>
      These limitations/exclusions to do not apply to users located in the EEA.
      For those users, if we fail to comply with the Terms of Service, we are
      responsible for loss or damage you suffer that is a foreseeable result of
      the breach of the Terms of Service or is a result of negligence, but we
      are not responsible for any loss or damage that is not foreseeable. Loss
      or damage is foreseeable if it was an obvious consequence of our breach or
      if it was contemplated by you and us at the time we entered into the Terms
      of Service.
    </p>
    <p>
      You will indemnify and hold harmless FLLC and its officers, directors,
      employee and agents, from and against any claims, disputes, demands,
      liabilities, damages, losses, and costs and expenses, including, without
      limitation, reasonable legal and accounting fees, arising out of or in any
      way connected with (i) your access to or use of the website or User
      Content or (ii) your violation of the Terms of Service.
    </p>

    <h3 id="15">DISPUTE RESOLUTION; CLASS ACTION WAIVER</h3>
    <p class="underline">Governing Law</p>
    <p>
      The governing law for the Terms of Service of Service and any action
      related thereto will be the laws of the State of Texas, without regard to
      its conflict of laws provisions.
    </p>
    <p class="underline">Trial by Jury and Class Action Waivers</p>
    <p>
      You acknowledge and agree that you and FLLC are each waiving the right to
      a trial by a judge or a jury or to participate as a plaintiff or class
      member in any purported class action or representative proceeding.
    </p>
    <p class="underline">Agreement to Arbitrate</p>
    <p>
      You and FLLC agree that any dispute, claim or controversy arising out of
      or relating to the Terms of Service or the breach, termination,
      enforcement, interpretation or validity thereof or the use of the Platform
      or Content (collectively, "Disputes") will be settled by binding
      arbitration, except that each party retains the right: (i) to bring an
      individual action in small claims court and (ii) to seek injunctive or
      other equitable relief in a court of competent jurisdiction to prevent the
      actual or threatened infringement, misappropriation or violation of a
      party's copyrights, trademarks, trade secrets, patents or other
      intellectual property rights. Without limiting the preceding sentence, you
      will also have the right to litigate any other Dispute if you provide us
      with written notice of your desire to do so by email at
      <a href="mailto:support@favoritely.com">support@favoritely.com</a>
      within thirty (30) days following the date you first agree to the Terms of
      Service (such notice, an "Arbitration Opt-out Notice"). If you don't
      provide us with an Arbitration Opt-out Notice within the thirty (30) day
      period, you will be deemed to have knowingly and intentionally waived your
      right to litigate any Dispute except as expressly set forth in clauses (i)
      and (ii) above. Further, unless both you and FLLC otherwise agree in
      writing, the arbitrator may not consolidate more than one person's claims,
      and may not otherwise preside over any form of any class or representative
      proceeding. If, however, this waiver of class or consolidated actions is
      deemed invalid or unenforceable, neither you nor FLLC are entitled to
      arbitration; instead all claims and disputes will be resolved in a court
      of competent jurisdiction. In any litigation between you and FLLC over
      whether to vacate or enforce an arbitration award, you and FLLC waive all
      rights to a jury trial, and elect instead to have the dispute be resolved
      by a judge. If this specific paragraph is held unenforceable, then the
      entirety of this "Dispute Resolution" section will be deemed void. Except
      as provided in the preceding sentence, this "Dispute Resolution" section
      will survive any termination of the Terms of Service.
    </p>
    <p class="underline">Arbitration Rules</p>
    <p>
      The arbitration will be administered by the American Arbitration
      Association ("AAA") in accordance with the Commercial Arbitration Rules
      and the Supplementary Procedures for Consumer Related Disputes (the "AAA
      Rules") then in effect, except as modified by this "Dispute Resolution"
      section. (The AAA Rules are available at
      <a href="https://www.adr.org/Rules">www.adr.org/arb_med</a>
      or by calling the AAA at 1-800-778-7879.) The Federal Arbitration Act will
      govern the interpretation and enforcement of this Section. The venue for
      any arbitration shall be Houston, Texas.
    </p>
    <p class="underline">Arbitration Process</p>
    <p>
      A party who desires to initiate arbitration must provide the other party
      with a written Demand for Arbitration as specified in the AAA Rules. (The
      AAA provides a general Demand for Arbitration form and a separate form for
      Demand for Arbitration for California residents.) The arbitrator will be
      either a retired judge or an attorney licensed to practice law and will be
      selected by the parties from the AAA's roster of arbitrators. If the
      parties are unable to agree upon an arbitrator within seven (7) days of
      delivery of the Demand for Arbitration, then the AAA will appoint the
      arbitrator in accordance with the AAA Rules.
    </p>
    <p class="underline">Arbitration Location and Procedure</p>
    <p>
      If the claim is for $10,000 or less, the party initiating the arbitration
      may choose whether the arbitration will be conducted (1) solely on the
      basis of documents submitted to the arbitrator; (2) through a
      non-appearance based telephonic hearing; or (3) by an in-person hearing as
      established by the AAA Rules in the county of your billing address. In the
      case of an in-person hearing, the proceedings will be conducted at a
      location which is reasonably convenient for both parties with due
      consideration of the ability to travel and other pertinent circumstances.
      If the parties are unable to agree on a location, the determination will
      be made by the arbitration institution. If your claim exceeds $10,000,
      your right to a hearing will be determined by the AAA Rules. Subject to
      the AAA Rules, the arbitrator will have the discretion to direct a
      reasonable exchange of information by the parties, consistent with the
      expedited nature of the arbitration.
    </p>
    <p class="underline">Arbitrator's Decision</p>
    <p>
      The arbitrator will decide the jurisdiction of the arbitration and the
      rights and liabilities, if any, of you and FLLC. The dispute will not be
      consolidated with any other matters or joined with any other cases or
      parties. The arbitrator will have the authority to grant motions
      dispositive of all or part of any claim or dispute. The arbitrator will
      have the authority to award all remedies available under applicable law,
      the arbitral forum's rules, and the Terms. The arbitrator has the same
      authority to award relief on an individual basis that a judge in a court
      of law would have. The award of the arbitrator is final and binding upon
      you FLLC.
    </p>
    <p class="underline">Fees</p>
    <p>
      Your arbitration fees and your share of arbitrator compensation will be
      limited to those fees set forth in the AAA Rules with the remainder split
      evenly between the parties, unless otherwise required by law. If the
      arbitrator finds that either the substance of your claim or the relief
      sought in the arbitration is frivolous or brought for an improper purpose
      (as measured by the standards set forth in Federal Rule of Civil Procedure
      11(b)), then the payment of all fees will be governed by the AAA Rules. In
      such case, you agree to reimburse us for all monies previously disbursed
      by it that are otherwise your obligation to pay under the AAA Rules.
      Regardless of the manner in which the arbitration is conducted, the
      arbitrator shall issue a reasoned written decision sufficient to explain
      the essential findings and conclusions on which the decision and award, if
      any, are based. The arbitrator may make rulings and resolve disputes as to
      the payment and reimbursement of fees or expenses at any time during the
      proceeding and upon request from either party made within 14 days of the
      arbitrator's ruling on the merits.
    </p>
    <p class="underline">Changes</p>
    <p>
      Notwithstanding the provisions of modifications provided above in Section
      2, if we change this "Dispute Resolution" section after the date you first
      accepted the Terms of Service (or accepted any subsequent changes to the
      Terms of Service), you may reject any such change by sending us written
      notice (including by email to
      <a href="mailto:support@favoritely.com">support@favoritely.com</a>
      within 30 days of the date such change became effective, as indicated in
      the "Last Updated" date above or in the date of our email to you notifying
      you of such change. By rejecting any change, you are agreeing that you
      will arbitrate any Dispute between you and us in accordance with the
      provisions of this "Dispute Resolution" section as of the date you first
      accepted the Terms of Service (or accepted any subsequent changes to the
      Terms of Service).
    </p>
    <p class="underline">Survival</p>
    <p>This Section 16 shall survive termination of the Terms of Service.</p>

    <h3 id="16">APPLICABLE LAW</h3>
    <p>
      If you are located in the United States, you agree that the Terms of
      Service affect interstate commerce and that the Federal Arbitration Act
      governs the interpretation and enforcement of Section 16 related to
      Dispute Resolution and Class Action Waiver.
    </p>
    <p>
      Further, our relationship and the Terms of Service will be governed by the
      law of the State of Texas, except for its conflict of laws principles.
    </p>
    <p>
      If you are located outside the United States, our relationship and the
      Terms of Service will be governed by Vienna International Arbitral Centre
      (VIAC), Vienna.
    </p>

    <h3 id="17">VENUE FOR LEGAL DISPUTES NOT SUBJECT TO ARBITRATION</h3>
    <p>
      Unless the parties agree to any other location, any judicial proceedings
      other than the small claims actions and the ones that are excluded from
      the Arbitration Agreement - Section 16, including any intellectual
      property or copyright infringement claims or, if you timely provide us
      with an Arbitration Opt-out Notice, must be brought in a state or a
      federal court in the State of Texas. You, FLLC and its Affiliate Entities
      all consent to personal jurisdiction and venue in the State of Texas.
    </p>

    <h3 id="18">SEPARATION OF TERMS</h3>
    <p>
      You and FLLC agree that the contents of each of the paragraphs of the
      Terms of Service operate separately. If any part of the Terms of Service,
      is unenforceable, the rest of the Terms of Service, shall still apply and
      shall be binding, and any part which is not an enforceable term will be
      replaced with a term or a clause that comes as legally and economically
      close to the unenforceable term as possible, with the possible exceptions
      as described in Section 16.
    </p>

    <h3 id="19">ASSIGNMENT</h3>
    <p>
      We may, with or without your consent, give its rights, or its obligations,
      under the Terms of Service, to any person or entity. You, however, without
      first getting our written consent, cannot give or sell your rights or your
      obligations under the Terms of Service to anyone, and any attempt to do so
      without our consent is void and actionable against you.
    </p>

    <h3 id="20">ENTIRE AGREEMENT</h3>
    <p>
      The Terms of Service, along with any other policies or rules referenced in
      the Terms of Service, make up the entire agreement between you and us
      relating to the subject matter present in the Terms of Service, and
      supersede all prior understandings, confirmations, and agreements of the
      parties relating to the subject matter of the Terms of Service,
      irrespective whether those prior understandings were oral or written,
      electronic, or whether established by any custom, practice, policy or
      precedent, between you and us.
    </p>

    <h3 id="21">LANGUAGE OF THE TERMS</h3>
    <p>
      The language used in the Terms of Service,
      <a href="/privacy">Privacy Policy</a>
      , or any other terms or policy is English. Any translated version of these
      documents provided to you is for informational purposes only. In case the
      translated version means something different from the English language
      version, then the English meaning will be applicable.
    </p>

    <h3 id="22">NO WAIVER</h3>
    <p>
      The failure of us to require or enforce strict performance by you of any
      provision of the Terms of Service or our
      <a href="/privacy">Privacy Policy</a>
      or failure to exercise any right under them shall not be construed as a
      waiver or relinquishment of our right to assert or rely upon any such
      provision or right in that or any other instance.
    </p>
    <p>
      The express waiver by us of any provision, condition, or requirement of
      the Terms of Service or our
      <a href="/privacy">Privacy Policy</a>
      shall not constitute a waiver of any future obligation to comply with such
      provision, condition or requirement.
    </p>
    <p>
      Except as expressly and specifically set forth in the Terms of Service, no
      representations, statements, consents, waivers, or other acts or omissions
      by us shall be deemed a modification of the Terms of Service nor legally
      binding, unless documented in physical writing, hand signed by You and our
      duly appointed officer.
    </p>

    <h3 id="23">WAIVER OF SECTION 1542</h3>
    <p>
      With respect to the releases of liability set out in the Terms of Service,
      you acknowledge that you understand the consequences of entering into the
      general release and discharge of all known and unknown claims as stated in
      the Terms of Service and that you are familiar with the provisions of
      section 1542 of the California Civil Code, which provides that:
    </p>
    <p>
      A general release does not extend to claims that the creditor or releasing
      party does not know or suspect to exist in his or her favor at the time of
      executing the release and that, if known by him or her, would have
      materially affected his or her settlement with the debtor or released
      party.
    </p>
    <p>
      You hereby waive all rights under section 1542 and under any other federal
      or state statutes or laws of similar effect.
    </p>

    <h3 id="24">NOTICES</h3>
    <p>
      We may send notice(s) to you of something according to the Terms of
      Service. Such notice(s) will be notified to you by (i) posting a message
      on our Platform, or (ii) sending you an e-mail, or (ii) other ways of
      communication based on the contact information you provide to us.
    </p>
    <p>
      If you have to give us notice of something according to the Terms of
      Service, the notice must be in writing and addressed to FLLC, 10737 Cutten
      Road, Houston, Texas 77066, unless we have provided a more specific way of
      notifying us.
    </p>

    <h3 id="25">FORCE MAJEURE</h3>
    <p>
      We will not be held liable for any damages or problems arising out of our
      control. Any damage or problem caused by act of God such as floods,
      natural disasters will not be covered by us. In addition to act of God,
      will also not cover any damages or problems caused by network
      infrastructure failures, pandemics, strikes, riots, war, terrorism, fire,
      embargoes, acts of civil or military authorities, accidents, or shortages
      of transportation, facilities, fuel, energy, labor, or materials.
    </p>
  </LayoutLegal>
</template>

<script setup>
  definePageMeta({
    skipAuth: true,
  })

  useHead({
    title: "General Terms of Service - favoritely.com",
    meta: [{ name: "description", content: "favoritely.com" }],
  })
</script>
